import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

export default function VerifyEmail() {
    const { verifyEmail } = useAuth();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [state, setState] = useState("verifying"); // verifying | ok | error
    const [err, setErr] = useState("");

    useEffect(() => {
        const token = params.get("token");
        if (!token) { setState("error"); setErr("No verification token in the link."); return; }
        (async () => {
            const res = await verifyEmail(token);
            if (res.ok) setState("ok");
            else { setState("error"); setErr(res.error || "Invalid or expired verification link."); }
        })();
    }, [params, verifyEmail]);

    return (
        <div className="min-h-screen grid place-items-center bg-sand-50 p-6">
            <div className="w-full max-w-md rounded-3xl bg-white border border-sand-200 shadow-card p-8 text-center">
                <div className="flex justify-center"><Link to="/"><Logo /></Link></div>
                <div className="mt-6 flex justify-center">
                    {state === "verifying" && <Loader2 className="h-10 w-10 text-secondary animate-spin" />}
                    {state === "ok" && <CheckCircle2 className="h-12 w-12 text-emerald-600" />}
                    {state === "error" && <XCircle className="h-12 w-12 text-coral-dark" />}
                </div>
                {state === "verifying" && (
                    <>
                        <h1 className="font-heading text-3xl text-ocean-700 mt-5">Verifying your email…</h1>
                        <p className="text-muted-foreground text-sm mt-2">Hang tight, this only takes a moment.</p>
                    </>
                )}
                {state === "ok" && (
                    <>
                        <h1 className="font-heading text-3xl text-ocean-700 mt-5" data-testid="verify-success">You're verified!</h1>
                        <p className="text-muted-foreground text-sm mt-2">
                            Your email has been confirmed. You're all set.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 bg-secondary/10 text-secondary-dark rounded-full px-3 py-1 text-xs font-medium">
                            <ShieldCheck className="h-3.5 w-3.5" /> Email verified
                        </div>
                        <Button onClick={() => navigate("/app")} className="w-full mt-6 h-11 bg-ocean-700 text-white rounded-xl" data-testid="verify-go-to-app">
                            Continue to dashboard
                        </Button>
                    </>
                )}
                {state === "error" && (
                    <>
                        <h1 className="font-heading text-3xl text-ocean-700 mt-5" data-testid="verify-error">Verification failed</h1>
                        <p className="text-muted-foreground text-sm mt-2">{err}</p>
                        <p className="text-xs text-muted-foreground mt-4">
                            Verification links expire after 24 hours. You can request a new one from your account banner after signing in.
                        </p>
                        <Button onClick={() => navigate("/login")} className="w-full mt-6 h-11 bg-ocean-700 text-white rounded-xl">
                            Back to sign in
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
